/**
 * OfflineBanner
 *
 * Sticky banner at the very top of the viewport that surfaces three
 * connectivity states: actively syncing, browser offline, or websocket
 * disconnected while the network is alive. It sits above the header rather than
 * over it — `body:has(.lms-offline-banner)` lifts the header, sider and content
 * by --layout-banner-height (app-shell.md §1), so it never hides the chrome.
 *
 * Each state is a 12% tint with a full-strength bottom border and matching ink;
 * offline moves onto garnet (the failure colour) so it no longer reads as the
 * same event as syncing.
 */

import { useEffect, useState } from 'react';
import {
  IconWifi,
  IconDisconnect,
  IconSync,
  IconClose,
} from './icons';
import { useTranslation } from 'react-i18next';
import '../layout/layout.css';

const OfflineBanner = ({ isOnline, isConnected, isSyncing }) => {
  const { t } = useTranslation();
  // The "reconnecting" banner is dismissible. We re-arm it on every successful
  // reconnect so a *later* drop surfaces it again — dismissing only hides the
  // current episode, not all future ones.
  const [reconnectDismissed, setReconnectDismissed] = useState(false);

  useEffect(() => {
    if (isConnected) setReconnectDismissed(false);
  }, [isConnected]);

  if (isOnline && isConnected && !isSyncing) return null;

  if (isSyncing) {
    return (
      <div className="lms-offline-banner lms-offline-banner--syncing" role="status">
        <IconSync spin className="lms-offline-banner__glyph" />
        <span className="lms-offline-banner__text">{t('network.syncing')}</span>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className="lms-offline-banner lms-offline-banner--offline" role="alert">
        <IconDisconnect className="lms-offline-banner__glyph" />
        {/* The long sentence wraps to three lines below 768, so the phone shows
            the short form instead (§1). CSS swaps them; both are rendered. */}
        <span className="lms-offline-banner__text lms-offline-banner__text--long">
          {t('network.offline')}
        </span>
        <span className="lms-offline-banner__text lms-offline-banner__text--short">
          {t('network.offlineShort')}
        </span>
      </div>
    );
  }

  if (!isConnected) {
    if (reconnectDismissed) return null;
    return (
      <div className="lms-offline-banner lms-offline-banner--reconnecting" role="status">
        <IconWifi className="lms-offline-banner__glyph" />
        <span className="lms-offline-banner__text">{t('network.reconnecting')}</span>
        <button
          type="button"
          className="lms-offline-banner__close"
          onClick={() => setReconnectDismissed(true)}
          aria-label={t('common.close', { defaultValue: 'Close' })}
        >
          <IconClose />
        </button>
      </div>
    );
  }

  return null;
};

export default OfflineBanner;
